import type { FilterPanelCheckItem } from 'components/Common/FilterPanel/FilterPanelCheckboxGroup'
import { STANDALONE_FLIGHT_FILTERS, FlightFilterType } from 'constants/flight'
import { arrayToObject, last } from 'lib/array/arrayUtils'
import { formatCurrency } from 'lib/format/formatCurrencyIntl'
import { convertMinutesToHoursAndMinutes, convertMinutesToTimeString } from 'lib/flights/flightUtils'
import { getFilterQuantity } from 'lib/flights/flightFilterUtils'
import { FlightsFiltersPriceType } from 'lib/flights/flightFilterTypes'

interface LabelDescriptor {
  label: string
  values: Array<string | number>
  keys: Array<FlightFilterType | string>
}

export interface FlightFilterLabelDescriptor extends LabelDescriptor {
  type: FlightFilterType
}

interface BuildParams {
  filters: Record<string, Array<FilterPanelCheckItem>>
  activeFilters: Record<string, Set<string>>
  currency: { code: string, region: string }
  airports: { origin: string, arrival: string }
  priceFilterType: FlightsFiltersPriceType
}

export function getFlightFilterLabelDescriptors({
  filters,
  activeFilters,
  currency,
  airports,
  priceFilterType,
}: BuildParams): Array<FlightFilterLabelDescriptor> {
  return STANDALONE_FLIGHT_FILTERS.flatMap(filterKey => {
    const descriptor = getFilterDescriptorForType(filterKey, {
      filters,
      activeFilters,
      currency,
      airports,
      priceFilterType,
    })

    if (!descriptor) return []
    const descriptors = Array.isArray(descriptor) ? descriptor : [descriptor]

    return descriptors
      .filter(desc => Boolean(desc.label))
      .map(desc => ({
        ...desc,
        type: filterKey,
      }))
  })
}

function getFilterDescriptorForType(
  filterKey: FlightFilterType,
  {
    filters,
    activeFilters,
    currency,
    airports,
    priceFilterType,
  }: BuildParams,
): LabelDescriptor | Array<LabelDescriptor> | undefined {
  const quantity = getFilterQuantity(activeFilters, filterKey)
  if (quantity === 0) {
    return undefined
  }

  if (filterKey === FlightFilterType.PRICES) {
    return buildPriceDescriptor(filters, activeFilters, currency, priceFilterType)
  }

  if (filterKey === FlightFilterType.TIMES) {
    return buildTimeDescriptors(activeFilters, airports)
  }

  if (filterKey === FlightFilterType.STOP_DURATIONS) {
    const value = firstSetValue(activeFilters[FlightFilterType.STOP_DURATIONS])
    if (!value) return undefined
    const time = convertMinutesToHoursAndMinutes(parseInt(value, 10))

    return {
      label: `Stopover time under ${time.hours}h ${time.minutes}m`,
      values: [value],
      keys: [FlightFilterType.STOP_DURATIONS],
    }
  }

  if (filterKey === FlightFilterType.DURATIONS) {
    const value = firstSetValue(activeFilters[FlightFilterType.DURATIONS])
    if (!value) return undefined
    const time = convertMinutesToHoursAndMinutes(parseInt(value, 10))

    return {
      label: `Flight duration under ${time.hours}h ${time.minutes}m`,
      values: [value],
      keys: [FlightFilterType.DURATIONS],
    }
  }

  const values = [...(activeFilters[filterKey] || new Set<string>())].filter(value => value !== 'all')
  if (!values.length) return undefined

  const labels = arrayToObject(
    filters[filterKey] || [],
    filter => filter.value,
    filter => filter.label as string,
  )

  return values.map(value => ({
    label: labels[value] ?? value,
    values: [value],
    keys: [filterKey],
  }))
}

function buildPriceDescriptor(
  filters: Record<string, Array<FilterPanelCheckItem>>,
  activeFilters: Record<string, Set<string>>,
  currency: { code: string, region: string },
  priceFilterType: FlightsFiltersPriceType,
): LabelDescriptor | undefined {
  const lastPriceOption = last(filters[FlightFilterType.PRICES])
  const lastPriceValue = typeof lastPriceOption?.value === 'string'
    ? parseInt(lastPriceOption.value, 10)
    : lastPriceOption?.value

  const minPriceValue = firstSetValue(activeFilters[FlightFilterType.MIN_PRICES])
  const maxPriceValue = firstSetValue(activeFilters[FlightFilterType.MAX_PRICES]) ?? (lastPriceValue?.toString())

  if (!minPriceValue && !maxPriceValue) return undefined

  const parsedMin = typeof minPriceValue === 'string' ? parseInt(minPriceValue, 10) : minPriceValue ?? 0
  const parsedMax = typeof maxPriceValue === 'string' ? parseInt(maxPriceValue, 10) : maxPriceValue ?? lastPriceValue ?? 0

  const min = Math.floor(parsedMin / 100) * 100
  const max = Math.round(parsedMax / 100) * 100

  const minPrice = formatCurrency(min, currency.code, currency.region)
  const maxPrice = formatCurrency(max, currency.code, currency.region)

  const labelPrefix = priceFilterType === FlightsFiltersPriceType.ADULT ? 'Per adult price' : 'Total price'

  return {
    label: `${labelPrefix} between ${minPrice} - ${maxPrice}`,
    values: [minPriceValue ?? '0', maxPriceValue ?? lastPriceValue ?? '0'],
    keys: [FlightFilterType.MIN_PRICES, FlightFilterType.MAX_PRICES],
  }
}

function buildTimeDescriptors(
  activeFilters: Record<string, Set<string>>,
  airports: { origin: string, arrival: string },
): Array<LabelDescriptor> | undefined {
  const descriptors: Array<LabelDescriptor> = []

  const minDepartValue = firstSetValue(activeFilters[FlightFilterType.MIN_DEPARTURE_TIME])
  const maxDepartValue = firstSetValue(activeFilters[FlightFilterType.MAX_DEPARTURE_TIME])

  if (minDepartValue || maxDepartValue) {
    const minDepartureTime = convertMinutesToTimeString(parseInt(minDepartValue || '0', 10))
    const maxDepartureTime = convertMinutesToTimeString(parseInt(maxDepartValue || '1440', 10))

    descriptors.push({
      label: `Depart from ${airports.origin} ${minDepartureTime} - ${maxDepartureTime}`,
      values: [minDepartValue ?? '0', maxDepartValue ?? '1440'],
      keys: [FlightFilterType.MIN_DEPARTURE_TIME, FlightFilterType.MAX_DEPARTURE_TIME],
    })
  }

  const minArrivalValue = firstSetValue(activeFilters[FlightFilterType.MIN_ARRIVAL_TIME])
  const maxArrivalValue = firstSetValue(activeFilters[FlightFilterType.MAX_ARRIVAL_TIME])

  if (minArrivalValue || maxArrivalValue) {
    const minArrivalTime = convertMinutesToTimeString(parseInt(minArrivalValue || '0', 10))
    const maxArrivalTime = convertMinutesToTimeString(parseInt(maxArrivalValue || '1440', 10))

    descriptors.push({
      label: `Arrive at ${airports.arrival} ${minArrivalTime} - ${maxArrivalTime}`,
      values: [minArrivalValue ?? '0', maxArrivalValue ?? '1440'],
      keys: [FlightFilterType.MIN_ARRIVAL_TIME, FlightFilterType.MAX_ARRIVAL_TIME],
    })
  }

  return descriptors.length ? descriptors : undefined
}

function firstSetValue(set?: Set<string>) {
  if (!set || set.size === 0) return undefined
  const [value] = set
  return value
}
